import React, {type ReactNode} from 'react';

import {type IssueLine} from '../../state/issues/types';
import {recordRumAction} from '../../util/datadog/datadogRum';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {PanelPopupMode} from '../Panel/types';
import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  type ExcludeOutliersValues,
  type SinglePointRenderMode,
} from '../WorkspaceDrawer/Settings/types';
import {getTitleFromConfig} from './common';
import {GraphWrapper} from './Graph';
import {getGraphAnnotations} from './graphAnnotations';
import {
  DEFAULT_GRAPH_LEGEND_POSITION,
  type GraphAxis,
  type GraphRenderProps,
} from './graphRenderProps';
import {getGraphSelection} from './graphSelection';
import {getGraphTooltip, getLegendRunLink} from './graphTooltip';
import {type IssueMarkerStateInputs} from './issueMarkerViewProps';
import {type LinePlotSettings} from './LinePlotSettingsContext';
import {type RunsLinePlotPanelInnerProps} from './types';
import {usePreparedChartSeries} from './usePreparedChartSeries';
import {type GraphZoomInputs} from './zoom/getGraphZoom';
import {usePreparedGraphZoom} from './zoom/usePreparedGraphZoom';

/** Existing application inputs, owned by the compatibility boundary. */
export type GraphProps = RunsLinePlotPanelInnerProps &
  Omit<
    GraphRenderProps,
    | 'annotations'
    | 'notices'
    | 'tooltip'
    | 'title'
    | 'zoom'
    | 'appearance'
    | 'rendering'
    | 'legend'
    | 'loading'
    | 'errors'
    | 'data'
    | 'series'
    | 'seriesStylesById'
    | 'seriesMetadataById'
    | 'axis'
    | 'config'
    | 'plotSettings'
    | 'fallbacks'
  > & {
    /** Application issue inputs are prepared before crossing the renderer boundary. */
    issueLines?: IssueLine[];
    issueMarkers: IssueMarkerStateInputs;
    cappedPanel: {
      isDelayed: boolean;
      setAllowUncappedMetrics: (allow: boolean) => void;
    };
    samplingWarning: {isSamplingFallback: boolean; warningMessages: string[]};
    disableRunLinks?: boolean;
    runNameTruncationType?: TruncationType;
    panelTitle: {
      isFullScreen: boolean;
      makeHighlightSearchTitle: ((title: string) => ReactNode) | undefined;
      setPanelPopupMode: (mode: PanelPopupMode) => void;
    };
    zoom: GraphZoomInputs;
    zoomUsage: {isFullScreen: boolean; isInReport: boolean};
    plotSettings: LinePlotSettings;
    display: {
      excludeOutliers: ExcludeOutliersValues | undefined;
      showLegend: boolean | undefined;
      singlePointRenderMode: SinglePointRenderMode | undefined;
    };
  };

/** Owns legacy snapshot preparation and selects the renderer's consumed fields. */
export function GraphAdapter(props: GraphProps) {
  const {series, seriesStylesById, seriesMetadataById} = usePreparedChartSeries(
    props.lines
  );
  const controlledZoom = usePreparedGraphZoom(props, series);
  function resetZoom() {
    controlledZoom.onZoom({intent: {shouldZoomX: false, shouldZoomY: false}});
  }
  const zoom: GraphRenderProps['zoom'] = {
    isActive: controlledZoom.activeZoom,
    isZooming: controlledZoom.zooming,
    isZoomedXMax: controlledZoom.isZoomedXMax,
    isZoomedXMin: controlledZoom.isZoomedXMin,
    onReset: resetZoom,
    onZoom: controlledZoom.onZoom,
    xDomain: controlledZoom.xDomain,
    yDomain: controlledZoom.yDomain,
  };
  function openPanelEditor() {
    props.panelTitle.setPanelPopupMode(PanelPopupMode.EDIT);
  }
  const titleText = getTitleFromConfig(props.config);
  const title: GraphRenderProps['title'] = {
    text: titleText,
    renderHighlightedText: props.panelTitle.isFullScreen
      ? undefined
      : props.panelTitle.makeHighlightSearchTitle,
    onActivate: openPanelEditor,
  };
  const annotationItems = props.issueLines?.map(({id, status, xValue}) => ({
    id,
    status,
    xValue,
  }));
  const annotations = getGraphAnnotations(annotationItems, props.issueMarkers);
  const status: {ready: boolean; loading: boolean; hasError?: boolean} = {
    ready: props.data.histories != null,
    loading: props.loading,
    hasError: props.hasError,
  };
  const selection = getGraphSelection(props.config);
  const axis: GraphAxis = {
    xKey: props.plotSettings.xAxis,
    xIsDatetime: props.plotSettings.xAxisIsDatetime,
    xTitle: props.config.xAxisTitle,
    xScale: props.plotSettings.xLogScale
      ? ('log' as const)
      : ('linear' as const),
    yTitle: props.config.yAxisTitle,
    yScale: props.plotSettings.yLogScale
      ? ('log' as const)
      : ('linear' as const),
    outlierPolicy: props.display.excludeOutliers,
  };
  const appearance: GraphRenderProps['appearance'] = {
    fontSize: props.config.fontSize ?? 'auto',
    isDarkMode: props.plotSettings.isDarkMode,
  };
  const rendering: GraphRenderProps['rendering'] = {
    exportMode: props.exportMode ?? false,
    forceReactVis: props.plotSettings.forceReactVis === true,
    isFullFidelity: props.plotSettings.isFullFidelity,
    pointStrategy: props.plotSettings.pointVisualizationMethod,
    plotType: props.plotSettings.plotType,
    allowUPlot: props.plotSettings.uplotEnabled,
  };
  const tooltip = getGraphTooltip(
    {
      colorPaletteOption: props.plotSettings.colorPaletteOption,
      xAxisFormat: props.plotSettings.xAxisFormat,
      colorRunNames: props.plotSettings.colorRunNames,
      displayFullRunName: props.plotSettings.displayFullRunName,
      entityName: props.plotSettings.entityName,
      getNamedWorkspaceSearch: props.plotSettings.getNamedWorkspaceSearch,
      hasMediaPanel: props.plotSettings.hasMediaPanel,
      highlightedCompanionRunOnly:
        props.plotSettings.highlightedCompanionRunOnly,
      isSingleRun: props.plotSettings.isSingleRun,
      onSnapMediaPanelsToStep: props.plotSettings.onSnapMediaPanelsToStep,
      projectName: props.plotSettings.projectName,
      showXRange: props.plotSettings.showXRange,
      singleRunNavParams: props.plotSettings.singleRunNavParams,
      tooltipNumberOfRuns: props.plotSettings.tooltipNumberOfRuns,
      xAxis: props.plotSettings.xAxis,
    },
    props.runNameTruncationType
  );
  const legend: GraphRenderProps['legend'] = {
    resolveRunLink: props.disableRunLinks ? undefined : getLegendRunLink,
    position: props.config.legendPosition ?? DEFAULT_GRAPH_LEGEND_POSITION,
    show: props.display.showLegend,
    colorPalette: props.plotSettings.colorPaletteOption,
    colorRunNames: props.plotSettings.colorRunNames,
    isSingleRun: props.plotSettings.isSingleRun,
    onHighlight: (runId, metricName) =>
      props.highlight.setHighlight('run:name', runId, metricName),
  };
  const panelId = props.panelId;
  const setAllowUncappedMetrics = props.cappedPanel.setAllowUncappedMetrics;
  function onDelayedShown() {
    recordRumAction('system_metrics_capped_panel_mounted', {
      title: titleText,
      panelId,
    });
  }
  function onLoadAnyway() {
    recordRumAction('system_metrics_capped_panel_displayed', {
      title: titleText,
      panelId,
    });
    setAllowUncappedMetrics(true);
  }
  const fetchedCounts = Object.values(
    props.data.fetchedRunCountByRunsetId ?? {}
  );
  const notices: GraphRenderProps['notices'] = {
    groupingDepth: props.plotSettings.groupingDepth,
    hasGroupedRunsets: props.plotSettings.isAnyRunsetGrouped,
    displayRowLimitPerRunset: props.plotSettings.limit,
    hasSubsampledGroups: props.data.hasSubsampledGroups ?? false,
    metricCount: props.config.metrics?.length ?? 0,
    showLimitHelp: !(props.readOnly ?? false),
    maxFetchedRunsPerRunset:
      fetchedCounts.length === 0 ? 0 : Math.max(...fetchedCounts),
    groupInputRunLimitPerRunset:
      props.config.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS,
    fetchedRunCount: props.data.fetchedRunCount ?? 0,
    runsetCount: fetchedCounts.length,
    groupedIncludedRunCount: props.data.totalGroupedIncludedRuns ?? 0,
    isSamplingFallback: props.samplingWarning.isSamplingFallback,
    samplingWarnings: props.samplingWarning.warningMessages,
    isDelayed: props.cappedPanel.isDelayed ? true : undefined,
    onDelayedNoticeShown: props.cappedPanel.isDelayed
      ? onDelayedShown
      : undefined,
    onLoadAnyway: props.cappedPanel.isDelayed ? onLoadAnyway : undefined,
  };
  const plotSettings: GraphRenderProps['plotSettings'] = {
    isMultiMetric: props.plotSettings.isMultiMetric,
  };
  const fallbacks: GraphRenderProps['fallbacks'] = {
    barYMin: props.config.yAxisMin,
    barYMax: props.config.yAxisMax,
    singlePointStyle: props.display.singlePointRenderMode,
  };
  const loading: GraphRenderProps['loading'] = {
    isActive: status.loading,
    isReady: status.ready,
    regexSettlingKey: selection.regexDataKey,
  };
  const errors: GraphRenderProps['errors'] = {
    hasUnexpectedError: status.hasError,
    hasExpressions: selection.hasExpressions,
    metricKeys: selection.metrics,
    savedXKey: props.config.xAxis,
  };
  return (
    <GraphWrapper
      axis={axis}
      appearance={appearance}
      rendering={rendering}
      notices={notices}
      legend={legend}
      highlight={props.highlight}
      annotations={annotations}
      series={series}
      seriesStylesById={seriesStylesById}
      seriesMetadataById={seriesMetadataById}
      panelId={props.panelId}
      title={title}
      tooltip={tooltip}
      plotSettings={plotSettings}
      sizeRef={props.sizeRef}
      zoom={zoom}
      fallbacks={fallbacks}
      loading={loading}
      errors={errors}
    />
  );
}
